import get from 'lodash/get';
import Logger from 'observability/Logger';
import {
    EMBED_CONFIG_VERSION,
    EMBED_CONFIG_VERSION_1_0_0,
    EMBED_CONFIG_VERSION_2_0_0,
} from '../../constants/embedConfig';

const LOG = new Logger('videoEmbedHelpers');

const withIframe = (src, htmlAttributes = '') => `
    <iframe
        width="100%"
        height="100%"
        src="${src}"
        frameborder="0"
        allowfullscreen
        referrerpolicy="unsafe-url"
        ${htmlAttributes}></iframe>
`;

export const createVideoEmbedCode = (videoUrl) =>
    withIframe(
        videoUrl,
        `allow="autoplay; encrypted-media; gyroscope; picture-in-picture"
           allowfullscreen title="Video Embed" `,
    );

export const createChatEmbedCode = (videoUrl) => withIframe(videoUrl, 'title="Chat Embed"');

/**
 * Parses the video ID from a YouTube URL
 *
 * @param videoUrl
 * @returns {null|String}
 */
export const parseYouTubeId = (videoUrl) => {
    try {
        // Check for short URLs first
        const shortUrl = 'youtu.be/';
        if (videoUrl.includes(shortUrl)) {
            return videoUrl.split(shortUrl).pop();
        }
        // Check for standard/long URLs
        const pattern = /.*v=([^&|\n|\t\s]+)/i;
        const res = videoUrl.match(pattern);
        return res[1] || null;
    } catch (e) {
        return null;
    }
};

const getConfigVersion = (embedConfig) => get(embedConfig, 'version', EMBED_CONFIG_VERSION);

/* eslint-disable prefer-template, comma-dangle */
const UnknownVersionError = (version, embedConfig) =>
    new Error(
        `
    Unknown config version ${version} in config ` + JSON.stringify(embedConfig, null, 4),
    );

export const getVimeoVideoId = (embedConfig, embedOptions) => {
    const version = getConfigVersion(embedConfig);
    switch (version) {
        case EMBED_CONFIG_VERSION_1_0_0:
            return get(embedOptions, 'vimeoVideoId', '');
        case EMBED_CONFIG_VERSION_2_0_0:
            return get(embedOptions, 'vimeo.videoId', '');
        default:
            LOG.warn(UnknownVersionError(version, embedConfig));
            return '';
    }
};

export const getVimeoChatEnabled = (embedConfig, embedOptions) => {
    const version = getConfigVersion(embedConfig);
    switch (version) {
        case EMBED_CONFIG_VERSION_1_0_0:
            return get(embedOptions, 'vimeoChatEnabled', false);
        case EMBED_CONFIG_VERSION_2_0_0:
            return get(embedOptions, 'vimeo.chatEnabled', false);
        default:
            LOG.warn(UnknownVersionError(version, embedConfig));
            return false;
    }
};

export const getYouTubeVideoId = (embedConfig, embedOptions) => {
    const version = getConfigVersion(embedConfig);
    switch (version) {
        case EMBED_CONFIG_VERSION_1_0_0:
            return get(embedOptions, 'youtubeVideoId', '');
        case EMBED_CONFIG_VERSION_2_0_0:
            return get(embedOptions, 'youtube.videoId', '');
        default:
            LOG.warn(UnknownVersionError(version, embedConfig));
            return '';
    }
};

export const getYouTubeChatEnabled = (embedConfig, embedOptions) => {
    const version = getConfigVersion(embedConfig);
    switch (version) {
        case EMBED_CONFIG_VERSION_1_0_0:
            return get(embedOptions, 'youtubeChatEnabled', false);
        case EMBED_CONFIG_VERSION_2_0_0:
            return get(embedOptions, 'youtube.chatEnabled', false);
        default:
            LOG.warn(UnknownVersionError(version, embedConfig));
            return false;
    }
};

/* eslint-disable-next-line arrow-body-style */
export const getYouTubeUserInputUrl = (embedOptions) => {
    // Property "userInputUrl" was introduced in v2.0.0,
    // so no need to check version
    return get(embedOptions, 'youtube.userInputUrl', '');
};

export const getZoomMeetingId = (embedConfig, embedOptions) => {
    const version = getConfigVersion(embedConfig);
    switch (version) {
        case EMBED_CONFIG_VERSION_1_0_0:
            return get(embedOptions, 'meetingId', '');
        case EMBED_CONFIG_VERSION_2_0_0:
            return get(embedOptions, 'zoom.meetingId', '');
        default:
            LOG.warn(UnknownVersionError(version, embedConfig));
            return '';
    }
};

export const getZoomMeetingPassword = (embedConfig, embedOptions) =>
    // Property "meetingPassword" was introduced in v2.0.0,
    // so no need to check version
    get(embedOptions, 'zoom.meetingPassword', '');

export const getZoomSignature = (embedConfig, embedOptions) => {
    const version = getConfigVersion(embedConfig);
    switch (version) {
        // Signature was added in v2.0.0, so there's no v1* specific key
        case EMBED_CONFIG_VERSION_1_0_0:
        case EMBED_CONFIG_VERSION_2_0_0:
            return get(embedOptions, 'zoom.signature', '');
        default:
            LOG.warn(UnknownVersionError(version, embedConfig));
            return '';
    }
};

export const getZoomChatEnabled = (embedConfig, embedOptions) =>
    // Property "chatEnabled" was introduced in v2.0.0,
    // so no need to check version
    get(embedOptions, 'zoom.chatEnabled', true);

export const getZoomShowGuestEmailsEnabled = (embedConfig, embedOptions) =>
    // Property "showGuestEmailsEnabled" was introduced in v2.0.0,
    // so no need to check version
    get(embedOptions, 'zoom.showGuestEmailsEnabled', false);

export const getIntrovokeEventId = (embedConfig, embedOptions) => get(embedOptions, 'introvoke.meetingId', '');

export const getIntrovokeChatEnabled = (embedConfig, embedOptions) => get(embedOptions, 'introvoke.chatEnabled', true);
export const getIntrovokeRandomUserMingleEnabled = (embedOptions) =>
    get(embedOptions, 'introvoke.customSettings.networkingHub.enableRandomUserMingle', false);
export const getIntrovokeMaxDurationValue = (embedOptions) =>
    get(embedOptions, 'introvoke.customSettings.networkingHub.privateConversationTimeLimit', null);
export const getIntrovokeIsNetworkingHubEnabled = (embedOptions) =>
    get(embedOptions, 'introvoke.customSettings.enableNetworkingHub', null);
export const GALLERY_VIEW_ENABLED = 'galleryViewEnabled';
export const getZoomGalleryViewEnabled = (embedOptions) => get(embedOptions, ['zoom', GALLERY_VIEW_ENABLED], false);
